import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';

import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { useEffect, useState, useRef } from 'react';

import { RHFTextField } from 'src/components/hook-form';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/auth/hooks';
import { useParams } from 'src/routes/hooks';

import {
  getTelecarePackage,
  updateTelecarePackage,
} from 'src/services/telehealth-packages.service';
import { searchTeleHealthTherapies } from 'src/services/telehealth.service';
import { getQuestionsById, getQuestionTypes } from 'src/services/questions.service';

export default function EditTherapyPackageView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { telepackagesId } = useParams();

  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [therapyOptions, setTherapyOptions] = useState([]);
  const [therapyInput, setTherapyInput] = useState('');
  const [selectedTherapies, setSelectedTherapies] = useState([]);
  const therapyDebounce = useRef(null);

  const [questionOptions, setQuestionOptions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const schema = Yup.object().shape({
    package_name: Yup.string().required(),
    starting_from: Yup.number().min(0).required(),
    visible_results_in_weeks: Yup.number().min(1).required(),
    description: Yup.string().nullable(),
    therapy_ids: Yup.array().of(Yup.string()).min(1).required(),
    promo_image: Yup.array().of(Yup.string().nullable()),
    questions_id: Yup.string().required(),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      package_name: '',
      starting_from: null,
      visible_results_in_weeks: null,
      description: '',
      therapy_ids: [],
      promo_image: [],
      questions_id: '',
    },
  });

  const { reset, handleSubmit, watch, setValue, control } = methods;
  const promoImages = watch('promo_image');
  const fileName = `tele_package_${new Date().toISOString()}`;
  const folderName = `images/tele-packages/${user.clinic_id}`;

  // Load all question types
  useEffect(() => {
    const fetchQuestionTypes = async () => {
      try {
        const res = await getQuestionTypes();
        const options = (res || []).map((q) => ({ id: q._id, label: q.type }));
        setQuestionOptions(options);
      } catch (err) {
        console.error(err);
      }
    };
    fetchQuestionTypes();
  }, []);

  // Load package details
  useEffect(() => {
    const loadPackage = async () => {
      try {
        const pkg = await getTelecarePackage(telepackagesId, user.clinic_id);

        reset({
          package_name: pkg.package_name || '',
          starting_from: pkg.starting_from || null,
          visible_results_in_weeks: pkg.visible_results_in_weeks || null,
          description: pkg.description || '',
          therapy_ids: (pkg.therapy_ids || []).map((t) => t.id || t._id),
          promo_image: pkg.promo_image ? [pkg.promo_image] : [],
          questions_id: pkg.questions_id?._id || "", // always string
        });
        console.log('Form values after reset():', methods.getValues());
        // Set selected therapies for UI display
        setSelectedTherapies(
          (pkg.therapy_ids || []).map((t) => ({ id: t.id || t._id, name: t.therapy_name }))
        );

        // Set selected question for UI
        if (pkg.questions_id) {
          const question = await getQuestionsById(pkg.questions_id._id || pkg.questions_id);
          if (question) setSelectedQuestion({ id: question._id, label: question.type });
        }
      } catch (err) {
        setSnackbarMessage('Failed to load therapy package');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    loadPackage();
  }, [telepackagesId, reset, user.clinic_id,methods]);

  // Debounced therapy search
  useEffect(() => {
    if (therapyDebounce.current) clearTimeout(therapyDebounce.current);
    if (therapyInput && therapyInput.length >= 2) {
      therapyDebounce.current = setTimeout(() => {
        searchTeleHealthTherapies(therapyInput).then((res) => {
          const options = (res || []).map((t) => ({ id: t.id || t._id, name: t.therapy_name }));
          setTherapyOptions(options);
        });
      }, 300);
    } else setTherapyOptions([]);
    return () => therapyDebounce.current && clearTimeout(therapyDebounce.current);
  }, [therapyInput]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        clinic_id: user.clinic_id,
        promo_image: Array.isArray(data.promo_image) && data.promo_image.length > 0
          ? data.promo_image[0]
          : "", // always string
      };
      await updateTelecarePackage(telepackagesId, payload, user.clinic_id);
      setSnackbarMessage('Therapy package updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate(-1);
    } catch (err) {
      console.error('ERROR UPDATING PACKAGE:', err);
      setSnackbarMessage(err.response?.data?.message || err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ maxWidth: 840, mt: 4, mb: 4, background: 'white', p: 3, borderRadius: 4 }}>
      <Stack direction="row" alignItems="center" gap={2} mb={2}>
        <IconButton onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Edit Therapy Package</Typography>
      </Stack>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: 2, lg: 4 }}>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="package_name" label="Package Name" required />
            </Grid>
            <Grid item xs={12} lg={6}>
              <RHFTextField name="starting_from" label="Starting Price" type="number" required />
            </Grid>
            <Grid item xs={12} lg={6}>
              <RHFTextField
                name="visible_results_in_weeks"
                label="Visible Results (Weeks)"
                type="number"
                required
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Controller
                name="questions_id"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={questionOptions}
                    getOptionLabel={(option) => option.label}
                    value={questionOptions.find((q) => q.id === field.value) || null}
                    onChange={(_, value) => {
                      field.onChange(value?.id || ""); // never null
                      setSelectedQuestion(value || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Assessment Type" required />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="therapy_ids"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={therapyOptions}
                    getOptionLabel={(option) => option.name}
                    inputValue={therapyInput}
                    onInputChange={(_, value) => setTherapyInput(value)}
                    value={selectedTherapies}
                    onChange={(_, value) => {
                      setSelectedTherapies(value);
                      field.onChange(value.map((v) => v.id));
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} label="Search Therapies" />}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <RHFTextField name="description" label="Description" multiline rows={4} />
            </Grid>

            <Grid item xs={12}>
              <RHFImageUpload
                initialImage={promoImages}
                fileName={fileName}
                setValue={setValue}
                name="promo_image"
                folderName={folderName}
                displayImages
                maxFiles={1}
                label="Promo Image"
                width={300}
                height={200}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ background: '#D92D7F', ':hover': { background: '#D92D7F' } }}
              >
                Update Therapy Package
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
